import { EditorState, Plugin, PluginKey, Transaction } from "prosemirror-state";
import { v4 } from "uuid";

import { Extension } from "~/lib/prose/extension";

export class PosBookmark {
	public readonly pos: number;
	public readonly id = v4();
	private discarded = false;
	private transactionsSince: Transaction[];

	constructor(
		pos: number,
		private factory: PosBookmarkFactory,
	) {
		this.pos = pos;
		this.transactionsSince = [];
	}

	recordTransactions(trs: readonly Transaction[]): void {
		this.transactionsSince.push(...trs);
	}

	discard() {
		this.discarded = true;
		this.factory.discardBookmark(this);
	}

	mapToNow(): number {
		if (this.discarded) throw new Error("bookmark is discarded");
		let pos = this.pos;
		for (const tr of this.transactionsSince) {
			pos = tr.mapping.map(pos);
		}
		return pos;
	}
}

class PosBookmarkFactory {
	private bookmarks = new Set<PosBookmark>();

	appendTransaction(trs: readonly Transaction[]): void {
		for (const bookmark of this.bookmarks) {
			bookmark.recordTransactions(trs);
		}
	}

	discardBookmark = (bookmark: PosBookmark) => {
		this.bookmarks.delete(bookmark);
	};

	create(pos: number): PosBookmark {
		const bookmark = new PosBookmark(pos, this);
		this.bookmarks.add(bookmark);
		return bookmark;
	}
}

const posBookmarkPluginKey = new PluginKey<PosBookmarkFactory>("posBookmarkPlugin");

/**
 * Record an initial position and any transactions that have happened since,
 * allowing to map the position over those transactions.
 *
 * This is useful when a change to the document requires some asynchronous
 * information, like a network request or user input. Make a bookmark, start
 * the async process, then map the position over all changes since the process
 * started.
 */
export function createPosBookmark(state: EditorState, pos: number): PosBookmark {
	const bookmarkFactory = posBookmarkPluginKey.getState(state);
	if (!bookmarkFactory) throw new Error("Can't create PosBookmark without PosBookmarkPlugin");
	return bookmarkFactory.create(pos);
}

const posBookmarkPlugin = new Plugin({
	key: posBookmarkPluginKey,
	state: {
		init: () => new PosBookmarkFactory(),
		apply: (_tr, value) => value,
	},
	appendTransaction(this: Plugin<PosBookmarkFactory>, trs, _oldState, newState) {
		this.getState(newState)?.appendTransaction(trs);
		return null;
	},
});

export class PosBookmarkExtension extends Extension {
	readonly name = "posbookmark";

	get plugins() {
		return [posBookmarkPlugin];
	}
}
